import { useTranslation } from 'react-i18next';
import { Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';
import { initialsOf } from '../../booking-page/helpers';
import type { Provider } from '@/types/booking';

// "Any available" plus one pill per person who offers the service. The step
// only shows it when two or more people offer the service.
export function PersonPicker({ providers, selected, onSelect }: {
  providers: Provider[];
  selected: Provider | null;
  onSelect: (provider: Provider | null) => void;
}) {
  const { t } = useTranslation('bookingPage');
  const pill = (key: string, active: boolean, onClick: () => void, content: React.ReactNode) => (
    <button
      key={key}
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'inline-flex h-11 shrink-0 items-center gap-2 whitespace-nowrap rounded-pill pl-1.5 pr-3.5 text-[14px] font-medium transition-colors',
        active ? 'border-[1.5px] border-brand-500 bg-brand-50 text-brand-700' : 'border border-line bg-surface text-ink hover:border-brand-300',
      )}
    >
      {content}
    </button>
  );

  return (
    <section className="flex flex-col gap-2.5" data-testid="person-picker">
      <span className="text-[14px] font-semibold text-ink">{t('steps.with')}</span>
      <div className="-mr-4 flex gap-2 overflow-x-auto pb-1 scrollbar-none sm:mr-0 sm:flex-wrap sm:overflow-visible">
        {pill('any', !selected, () => onSelect(null), (
          <>
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-pill bg-surface">
              <Sparkles className="h-4 w-4 text-brand-500" />
            </span>
            {t('steps.anyAvailable')}
          </>
        ))}
        {providers.map((p) => pill(String(p.id), selected?.id === p.id, () => onSelect(p), (
          <>
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-pill bg-brand-100 text-[12px] font-semibold text-brand-700" aria-hidden="true">
              {initialsOf(p.displayName)}
            </span>
            {p.displayName}
          </>
        )))}
      </div>
    </section>
  );
}
